"use client";

import { useMemo, useState } from "react";
import {
    Avancer,
    Curseur,
    Ecran,
    Montant,
    Question,
    Recommencer,
    Resultat,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { joursFactures, joursFeriesEnSemaine, tarifJournalier } from "@/lib/tools/dayRate";
import { expliquerTarif } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { SEUIL_TAXE_CHAMBRE } from "@/lib/tools/microEnterprise";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Combien facturer : du revenu qu'on veut garder au tarif d'une journée.
 *
 * Le calculateur de revenu net répond à « combien me reste-t-il ? ». Celui-ci
 * répond à la question qu'on se pose AVANT, en fixant son prix : combien
 * demander par jour pour garder tant chaque mois ? Tout le calcul vit dans
 * lib/tools/dayRate.js, qui inverse revenuNet au lieu de le refaire : les deux
 * outils ne peuvent pas se contredire.
 *
 * LA VENTE EST ÉCARTÉE D'ENTRÉE, avec son motif : le prix d'une marchandise se
 * construit sur son coût d'achat, pas sur des journées.
 *
 * AU-DELÀ DU PLAFOND, AUCUN TARIF N'EST AFFICHÉ. Il supposerait des taux que le
 * régime réel n'applique pas. Le résultat dit à la place ce que la micro peut
 * laisser au plus, et le curseur permet de redescendre.
 */

const PHASES = {
    activite: 0,
    vente: 0,
    artisan: 0,
    objectif: 1,
    frais: 1,
    montantFrais: 1,
    jours: 2,
    conges: 2,
    couple: 3,
    enfants: 3,
    caseT: 3,
    autres: 3,
    montantAutres: 3,
    option: 4,
    resultat: 4,
};

const L = {
    fr: {
        phases: ["Votre activité", "Votre objectif", "Votre temps", "Votre foyer", "Votre impôt"],
        retour: "Retour",
        continuer: "Continuer",
        recommencer: "Refaire le calcul",

        qActivite: "Quelle est votre activité ?",
        qActiviteAide:
            "Elle fixe votre taux de cotisations et la part de votre chiffre d'affaires qui sera imposée.",
        bnc: "Activité libérale",
        bncDetail: "Développement, design, conseil, rédaction, formation",
        services: "Prestations de services",
        servicesDetail: "Relevant des BIC : dépannage, bâtiment, coiffure, transport",
        cipav: "Profession libérale réglementée",
        cipavDetail: "Architecte, psychologue, ostéopathe, guide-conférencier",
        vente: "Vente de marchandises",
        venteDetail: "Achat-revente, boutique en ligne, restauration",

        venteTitre: "En vente, votre prix se construit sur votre marge",
        venteTexte:
            "Ce que vous gardez dépend de ce que vous coûtent vos marchandises, pas du temps passé à les vendre. Un tarif à la journée n'aurait donc pas de sens pour votre activité.",
        venteSuite:
            "Pour savoir ce que vous laisse un chiffre d'affaires en vente, cotisations et impôt compris, utilisez le calculateur de revenu net de cette rubrique.",
        venteRetour: "Choisir une autre activité",

        qArtisan: "Êtes-vous inscrit au répertoire des métiers ?",
        qArtisanAide:
            "Vos cotisations restent les mêmes, mais votre contribution à la formation professionnelle triple : 0,3 % au lieu de 0,1 %.",
        artisanOui: "Oui, je suis artisan",
        artisanOuiDetail: "Inscrit à la chambre de métiers et de l'artisanat",
        artisanNon: "Non, je suis commerçant",
        artisanNonDetail: "Inscrit au registre du commerce, ou ni l'un ni l'autre",

        qObjectif: "Combien voulez-vous garder chaque mois ?",
        qObjectifAide:
            "Ce qui vous reste une fois les cotisations, l'impôt et vos frais payés : l'équivalent de ce que vous vous versez pour vivre.",
        objectif: "Ce que vous voulez garder par mois",
        objectifAide: "Déplacez le curseur : votre tarif se recalcule.",

        qFrais: "Votre activité vous coûte-t-elle quelque chose chaque mois ?",
        qFraisAide:
            "En micro-entreprise, aucun frais ne vient réduire vos cotisations. C'est donc à votre tarif de les payer, en plus de ce que vous voulez garder.",
        fraisNon: "Presque rien",
        fraisNonDetail: "Un ordinateur déjà payé, des outils gratuits",
        fraisOui: "Oui, des abonnements ou du matériel",
        fraisOuiDetail: "Logiciels, assurance, téléphone, coworking",
        qMontantFrais: "Combien, en moyenne, par mois ?",
        qMontantFraisAide:
            "Ramenez au mois ce qui ne tombe qu'une fois par an : la CFE de décembre, ou un ordinateur que vous changez tous les trois ans.",
        frais: "Frais par mois",

        qJours: "Combien de jours par semaine facturez-vous à vos clients ?",
        qJoursAide:
            "Seulement les jours qu'un client vous paie. La prospection, les devis et la comptabilité vous prennent du temps, mais personne ne vous les règle.",
        jours5: "5 jours",
        jours5Detail: "Une mission à plein temps chez un client",
        jours4: "4 jours",
        jours4Detail: "Un jour reste pour la gestion et la prospection",
        jours3: "3 jours",
        jours3Detail: "Plusieurs petits clients à trouver",
        jours2: "2 jours",
        jours2Detail: "Une activité à côté d'un autre travail",
        jours1: "1 jour",
        jours1Detail: "Quelques missions ponctuelles",

        qConges: "Combien de semaines passez-vous sans facturer dans l'année ?",
        qCongesAide:
            "Vos vacances, mais aussi les creux entre deux missions et les jours d'arrêt. Les jours fériés sont déjà retirés : {feries} tombent en semaine en {annee}.",
        semaines: "Semaines sans facturer",
        joursRestants: "Il vous reste {jours} jours à facturer en {annee}.",

        qCouple: "Vivez-vous seul ou en couple ?",
        qCoupleAide:
            "Un couple marié ou pacsé déclare ensemble : le nombre de parts double, et le taux d'imposition baisse.",
        seul: "Seul",
        seulDetail: "Célibataire, divorcé ou veuf",
        couple: "En couple",
        coupleDetail: "Marié ou pacsé, déclaration commune",

        qEnfants: "Combien d'enfants à charge ?",
        qEnfantsAide:
            "Chaque enfant ajoute une demi-part, une part entière à partir du troisième, et votre impôt baisse d'autant.",
        enfants0: "Aucun",
        enfants1: "Un",
        enfants2: "Deux",
        enfants3: "Trois ou plus",

        qCaseT: "Élevez-vous seul vos enfants ?",
        qCaseTAide:
            "C'est la case T de votre déclaration : votre premier enfant vaut alors une part entière et non une demi-part.",
        caseTOui: "Oui, seul avec eux",
        caseTOuiDetail: "Personne d'autre ne vit à votre foyer",
        caseTNon: "Non",
        caseTNonDetail: "Vous vivez en concubinage, ou l'autre parent est au foyer",

        qAutres: "D'autres revenus dans le foyer ?",
        qAutresAide:
            "Un salaire, une pension, les revenus de votre conjoint. Ils peuvent vous faire changer de tranche d'imposition, et le chiffre d'affaires qu'il vous faut monte avec elle.",
        autresNon: "Non, seulement la micro",
        autresNonDetail: "Aucun autre revenu imposable",
        autresOui: "Oui",
        autresOuiDetail: "Salaire, pension, revenus du conjoint",
        qMontantAutres: "Combien, au total, sur l'année ?",
        qMontantAutresAide:
            "Le net imposable du foyer hors micro-entreprise, celui qui figure sur vos déclarations.",
        autresRevenus: "Autres revenus du foyer",

        qOption: "Avez-vous opté pour le versement libératoire ?",
        qOptionAide:
            "Votre impôt est alors prélevé avec vos cotisations, à taux fixe sur votre chiffre d'affaires, au lieu d'être calculé au barème.",
        optionOui: "Oui, j'ai opté",
        optionOuiDetail: "Impôt payé à l'Urssaf, à chaque déclaration",
        optionNon: "Non, je suis au barème",
        optionNonDetail: "C'est le cas par défaut",

        resultatTitre: "Votre tarif",
        tarif: "Facturez au moins",
        parJour: "par jour",
        tarifDetail:
            "Sur vos {jours} jours facturés, cela fait {ca} de chiffre d'affaires dans l'année, soit {caMois} par mois.",
        cotisations: "Cotisations sociales",
        impot: "Impôt sur le revenu",
        impotAide: "Ce que votre foyer paie en plus du fait de cette activité.",
        cfp: "Contribution à la formation professionnelle",
        fraisLigne: "Vos frais",
        net: "Il vous reste",
        netAide: "{montant} par mois, comme vous le vouliez",

        plafondTitre: "Cet objectif dépasse ce que la micro-entreprise peut vous laisser",
        plafondTexte:
            "Il vous faudrait encaisser {ca} dans l'année, au-delà du plafond de {plafond}. Dépassé deux années de suite, ce plafond vous fait passer au régime réel, où ce calcul ne vaut plus.",
        plafondMax: "Sous le plafond, votre activité vous laisse au plus {max} par mois.",

        tvaFranchise:
            "Vous restez sous {base} de chiffre d'affaires : vos factures ne portent pas de TVA, et ce tarif est le prix que paie votre client.",
        tvaJanvier:
            "Ce tarif s'entend hors taxe : au-delà de {base} encaissés dans l'année, vous facturez la TVA à partir du 1er janvier suivant.",
        tvaImmediate:
            "Ce tarif s'entend hors taxe : à ce chiffre d'affaires, vous facturez la TVA dès que vous franchissez {majore} la première année, puis dès le 1er janvier les années suivantes.",
        tvaClients:
            "Un client professionnel récupère cette TVA. Un particulier, lui, paie 20 % de plus, à moins que vous ne baissiez votre tarif pour l'absorber. Seule une activité exonérée de TVA y échappe.",

        taxeChambre:
            "S'y ajoutent les taxes de chambre consulaire, non chiffrées ici : leur taux dépend de votre activité et de votre chambre. Elles ne sont dues ni la première année, ni en dessous de {seuil} de chiffre d'affaires.",
        note: "Pour ce calcul, nous avons supposé une année pleine sans ACRE, qui réduirait vos cotisations les premiers trimestres. La contribution à la formation n'est pas due la première année.",
    },
    en: {
        phases: ["Your activity", "Your goal", "Your time", "Your household", "Your tax"],
        retour: "Back",
        continuer: "Continue",
        recommencer: "Start over",

        qActivite: "What is your activity?",
        qActiviteAide:
            "It sets your contribution rate and the share of your turnover that gets taxed.",
        bnc: "Liberal profession",
        bncDetail: "Development, design, consulting, writing, training",
        services: "Services",
        servicesDetail: "Commercial (BIC): repairs, building, hairdressing, transport",
        cipav: "Regulated liberal profession",
        cipavDetail: "Architect, psychologist, osteopath, tour guide",
        vente: "Sale of goods",
        venteDetail: "Retail, online shop, catering",

        venteTitre: "When you sell goods, your price is built on your margin",
        venteTexte:
            "What you keep depends on what your goods cost you, not on the time spent selling them. A day rate would make no sense for your activity.",
        venteSuite:
            "To find out what a turnover from selling goods leaves you, contributions and tax included, use the net income calculator in this section.",
        venteRetour: "Choose another activity",

        qArtisan: "Are you on the trades register?",
        qArtisanAide:
            "Your contributions stay the same, but your vocational training contribution triples: 0.3% instead of 0.1%.",
        artisanOui: "Yes, I am a tradesperson",
        artisanOuiDetail: "Registered with the chamber of trades",
        artisanNon: "No, I am a trader",
        artisanNonDetail: "On the trade register, or on neither",

        qObjectif: "How much do you want to keep each month?",
        qObjectifAide:
            "What is left once contributions, tax and your expenses are paid: the equivalent of what you pay yourself to live on.",
        objectif: "What you want to keep each month",
        objectifAide: "Move the slider: your rate recalculates.",

        qFrais: "Does your activity cost you anything each month?",
        qFraisAide:
            "In a micro-entreprise, no expense reduces your contributions. Your rate has to pay for them, on top of what you want to keep.",
        fraisNon: "Next to nothing",
        fraisNonDetail: "A computer already paid for, free tools",
        fraisOui: "Yes, subscriptions or equipment",
        fraisOuiDetail: "Software, insurance, phone, coworking",
        qMontantFrais: "How much, on average, per month?",
        qMontantFraisAide:
            "Spread over the months what only comes once a year: the business property levy in December, or a computer you replace every three years.",
        frais: "Expenses per month",

        qJours: "How many days a week do you invoice to clients?",
        qJoursAide:
            "Only the days a client pays you for. Prospecting, quotes and bookkeeping take time, but nobody pays you for them.",
        jours5: "5 days",
        jours5Detail: "A full-time assignment with one client",
        jours4: "4 days",
        jours4Detail: "One day stays for admin and prospecting",
        jours3: "3 days",
        jours3Detail: "Several small clients to find",
        jours2: "2 days",
        jours2Detail: "A side activity next to another job",
        jours1: "1 day",
        jours1Detail: "A few one-off assignments",

        qConges: "How many weeks a year do you spend without invoicing?",
        qCongesAide:
            "Your holidays, but also the gaps between assignments and days off sick. Public holidays are already taken out: {feries} fall on a weekday in {annee}.",
        semaines: "Weeks without invoicing",
        joursRestants: "You have {jours} days left to invoice in {annee}.",

        qCouple: "Do you live alone or with a partner?",
        qCoupleAide:
            "A married or civil-partnered couple files together: the number of shares doubles and the tax rate falls.",
        seul: "Alone",
        seulDetail: "Single, divorced or widowed",
        couple: "With a partner",
        coupleDetail: "Married or in a civil partnership, joint return",

        qEnfants: "How many dependent children?",
        qEnfantsAide:
            "Each child adds half a share, a full one from the third, and your tax falls accordingly.",
        enfants0: "None",
        enfants1: "One",
        enfants2: "Two",
        enfants3: "Three or more",

        qCaseT: "Do you raise your children alone?",
        qCaseTAide:
            "This is box T on the French return: your first child then counts as a full share rather than half a share.",
        caseTOui: "Yes, alone with them",
        caseTOuiDetail: "Nobody else lives in your household",
        caseTNon: "No",
        caseTNonDetail: "You live with a partner, or the other parent is in the household",

        qAutres: "Any other household income?",
        qAutresAide:
            "A salary, a pension, your partner's income. They can move you into a higher tax band, and the turnover you need rises with it.",
        autresNon: "No, only the micro",
        autresNonDetail: "No other taxable income",
        autresOui: "Yes",
        autresOuiDetail: "Salary, pension, partner's income",
        qMontantAutres: "How much, in total, over the year?",
        qMontantAutresAide:
            "The household's taxable net excluding the micro-entreprise, as shown on your returns.",
        autresRevenus: "Other household income",

        qOption: "Have you opted for the flat-rate tax payment?",
        qOptionAide:
            "Your tax is then collected with your contributions, at a fixed rate on your turnover, instead of being computed on the scale.",
        optionOui: "Yes, I opted in",
        optionOuiDetail: "Tax paid to URSSAF with each return",
        optionNon: "No, I am on the scale",
        optionNonDetail: "That is the default",

        resultatTitre: "Your rate",
        tarif: "Invoice at least",
        parJour: "a day",
        tarifDetail:
            "Over your {jours} invoiced days, that makes {ca} of turnover for the year, or {caMois} a month.",
        cotisations: "Social contributions",
        impot: "Income tax",
        impotAide: "What your household pays extra because this activity exists.",
        cfp: "Vocational training contribution",
        fraisLigne: "Your expenses",
        net: "You keep",
        netAide: "{montant} a month, as you wanted",

        plafondTitre: "This goal is more than a micro-entreprise can leave you",
        plafondTexte:
            "You would need to collect {ca} over the year, above the {plafond} ceiling. Exceeded two years in a row, this ceiling moves you to the standard regime, where this calculation no longer holds.",
        plafondMax: "Below the ceiling, your activity leaves you at most {max} a month.",

        tvaFranchise:
            "You stay below {base} of turnover: your invoices carry no VAT, and this rate is the price your client pays.",
        tvaJanvier:
            "This rate excludes VAT: above {base} collected in the year, you charge VAT from the following 1 January.",
        tvaImmediate:
            "This rate excludes VAT: at this turnover you charge VAT as soon as you pass {majore} in the first year, then from 1 January in the following years.",
        tvaClients:
            "A business client recovers this VAT. A private client pays 20% more, unless you lower your rate to absorb it. Only a VAT-exempt activity escapes this.",

        taxeChambre:
            "Chamber levies come on top, not quantified here: their rate depends on your activity and your chamber. They are due neither in the first year, nor below {seuil} of turnover.",
        note: "For this calculation, we have assumed a full year without ACRE, which would cut your contributions for the first quarters. The training contribution is not due in the first year.",
    },
};

/** Une ligne de la cascade : un libellé, une pastille de couleur, un montant retranché. */
function Ligne({ libelle, aide, montant, couleur, lang, reduit, explication, ouvert, onBasculer }) {
    return (
        <div className="py-2.5 border-b border-gray-200/70 dark:border-white/10 last:border-0">
            <div className="flex justify-between items-start gap-4">
                <div className="flex items-start gap-2.5 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 ${couleur}`} />
                    <div className="min-w-0">
                        <div className="text-sm dark:text-white">{libelle}</div>
                        {aide && (
                            <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-0.5">
                                {aide}
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-sm font-semibold tabular-nums dark:text-white shrink-0">
                    −{" "}
                    <MontantExplicable explication={explication} ouvert={ouvert} onBasculer={onBasculer}>
                        <Montant valeur={montant} lang={lang} reduit={reduit} />
                    </MontantExplicable>
                </div>
            </div>
            {/* Le calcul se déplie sous la ligne, dans le fil de la lecture. */}
            {ouvert && <PanneauExplication explication={explication} lang={lang} />}
        </div>
    );
}

export default function CombienFacturer({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;

    // Les jours fériés sont ceux de l'année où la page est rendue : la date
    // vient du serveur, jamais d'un new Date() lu dans le navigateur.
    const annee = new Date(aujourdhui).getUTCFullYear();

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // La vente n'est pas calculée ici (l'écran « vente » renvoie ailleurs) :
    // une activité de vente du dossier laisse la question ouverte.
    const { valeurs: dossier } = useDossierOutil();
    const activiteDossier = dossier.activite === "vente" ? undefined : dossier.activite;
    const [activite, setActivite] = useState(activiteDossier ?? null);
    const [artisan, setArtisan] = useState(dossier.artisan ?? false);
    const [netMensuel, setNetMensuel] = useState(dossier.netMensuel ?? 2500);
    const [fraisMensuels, setFraisMensuels] = useState(dossier.fraisMensuels ?? 0);
    const [joursParSemaine, setJoursParSemaine] = useState(null);
    const [semainesConges, setSemainesConges] = useState(5);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [parentIsole, setParentIsole] = useState(false);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);
    const [optionFiscale, setOptionFiscale] = useState(dossier.optionFiscale ?? "bareme");

    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "activite",
        () => {
            setActivite(null);
            setArtisan(false);
            setNetMensuel(2500);
            setFraisMensuels(0);
            setJoursParSemaine(null);
            setSemainesConges(5);
            setEnCouple(false);
            setEnfants(0);
            setParentIsole(false);
            setAutresRevenus(0);
            setOptionFiscale("bareme");
        },
        PHASES,
        reprendre([
            { etape: "activite", connu: activiteDossier !== undefined },
            ...(activiteDossier === "services" ? [{ etape: "artisan", connu: dossier.artisan !== undefined }] : []),
            { etape: "objectif", connu: dossier.netMensuel !== undefined },
            { etape: "frais", connu: dossier.fraisMensuels !== undefined },
            ...(dossier.fraisMensuels > 0 ? [{ etape: "montantFrais", connu: true }] : []),
            // Le rythme de travail ne se déduit pas du dossier : le parcours s'y arrête.
            { etape: "jours", connu: false },
            { etape: "conges", connu: false },
            { etape: "couple", connu: dossier.enCouple !== undefined },
            { etape: "enfants", connu: dossier.enfants !== undefined },
            ...(!dossier.enCouple && dossier.enfants >= 1 ? [{ etape: "caseT", connu: false }] : []),
            { etape: "autres", connu: dossier.autresRevenus !== undefined },
            ...(dossier.autresRevenus > 0 ? [{ etape: "montantAutres", connu: true }] : []),
            { etape: "option", connu: dossier.optionFiscale !== undefined },
            { etape: "resultat" },
        ]),
    );

    useSignalerDossier(
        { activite, artisan, netMensuel, fraisMensuels, enCouple, enfants, autresRevenus, optionFiscale },
        etape === "resultat",
    );

    const r = useMemo(
        () =>
            activite && joursParSemaine
                ? tarifJournalier({
                      activite,
                      artisan,
                      optionFiscale,
                      autresRevenus,
                      enCouple,
                      enfants,
                      parentIsole,
                      netMensuel,
                      fraisMensuels,
                      joursParSemaine,
                      semainesConges,
                      annee,
                  })
                : null,
        [
            activite,
            artisan,
            optionFiscale,
            autresRevenus,
            enCouple,
            enfants,
            parentIsole,
            netMensuel,
            fraisMensuels,
            joursParSemaine,
            semainesConges,
            annee,
        ],
    );

    // Chaque montant du résultat dit d'où il vient. Le tarif déplie les jours
    // facturés, le chiffre d'affaires qu'il faut encaisser, puis la division.
    const ex = useMemo(() => {
        if (!r || r.tarifJour === null) return null;
        const parametres = { activite, artisan, optionFiscale, autresRevenus, enCouple, enfants, parentIsole };
        const e = expliquerTarif({ parametres, netMensuel, fraisMensuels, joursParSemaine, semainesConges, annee }, r, lang);
        return {
            ...e,
            tarif: {
                etapes: [...e.jours.etapes, e.chiffreAffaires.etapes[0], ...e.tarif.etapes],
                sources: e.jours.sources,
            },
        };
    }, [r, activite, artisan, optionFiscale, autresRevenus, enCouple, enfants, parentIsole, netMensuel, fraisMensuels, joursParSemaine, semainesConges, annee, lang]);
    const { ouverte, basculer } = useExplications();

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    if (etape === "activite") {
        return (
            <Question
                key={etape}
                {...communs}
                phases={t.phases}
                retour={t.retour}
                phase={PHASES.activite}
                titre={t.qActivite}
                aide={t.qActiviteAide}
                choix={["bnc", "services", "cipav", "vente"].map((cle) => ({
                    icone: {
                        bnc: "brain-linear",
                        services: "pen-2-linear",
                        cipav: "medal-ribbon-star-linear",
                        vente: "cart-large-linear",
                    }[cle],
                    titre: t[cle],
                    detail: t[`${cle}Detail`],
                    onClick: () => {
                        if (cle === "vente") {
                            aller("vente");
                            return;
                        }
                        setActivite(cle);
                        // Le répertoire des métiers ne change la contribution
                        // formation que pour les prestations commerciales.
                        aller(cle === "services" ? "artisan" : "objectif");
                    },
                }))}
            />
        );
    }

    if (etape === "vente") {
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.vente} titre={t.venteTitre}>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-3">{t.venteTexte}</p>
                <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.venteSuite}</p>
                <Avancer onClick={retour}>{t.venteRetour}</Avancer>
            </Ecran>
        );
    }

    if (etape === "artisan") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.artisan}
                titre={t.qArtisan}
                aide={t.qArtisanAide}
                choix={[
                    {
                        icone: "clipboard-check-linear",
                        titre: t.artisanOui,
                        detail: t.artisanOuiDetail,
                        onClick: () => {
                            setArtisan(true);
                            aller("objectif");
                        },
                    },
                    {
                        icone: "sale-linear",
                        titre: t.artisanNon,
                        detail: t.artisanNonDetail,
                        onClick: () => {
                            setArtisan(false);
                            aller("objectif");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "objectif") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.objectif}
                titre={t.qObjectif}
                aide={t.qObjectifAide}
            >
                <Curseur
                    libelle={t.objectif}
                    valeur={netMensuel}
                    poser={setNetMensuel}
                    minimum={500}
                    maximum={8000}
                    pas={100}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("frais")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "frais") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.frais}
                titre={t.qFrais}
                aide={t.qFraisAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.fraisNon,
                        detail: t.fraisNonDetail,
                        onClick: () => {
                            setFraisMensuels(0);
                            aller("jours");
                        },
                    },
                    {
                        icone: "bill-list-linear",
                        titre: t.fraisOui,
                        detail: t.fraisOuiDetail,
                        onClick: () => {
                            setFraisMensuels(150);
                            aller("montantFrais");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "montantFrais") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.montantFrais}
                titre={t.qMontantFrais}
                aide={t.qMontantFraisAide}
            >
                <Curseur
                    libelle={t.frais}
                    valeur={fraisMensuels}
                    poser={setFraisMensuels}
                    maximum={2000}
                    pas={10}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("jours")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "jours") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.jours}
                titre={t.qJours}
                aide={t.qJoursAide}
                choix={[5, 4, 3, 2, 1].map((n) => ({
                    icone: {
                        5: "case-round-linear",
                        4: "calendar-linear",
                        3: "users-group-rounded-linear",
                        2: "laptop-linear",
                        1: "cup-hot-linear",
                    }[n],
                    titre: t[`jours${n}`],
                    detail: t[`jours${n}Detail`],
                    onClick: () => {
                        setJoursParSemaine(n);
                        aller("conges");
                    },
                }))}
            />
        );
    }

    if (etape === "conges") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.conges}
                titre={t.qConges}
                aide={remplir(t.qCongesAide, { feries: joursFeriesEnSemaine(annee), annee })}
            >
                <Curseur
                    libelle={t.semaines}
                    valeur={semainesConges}
                    poser={setSemainesConges}
                    maximum={16}
                    pas={1}
                    formater={(v) => String(v)}
                />
                <p className="text-sm font-semibold dark:text-white mt-4 mb-0">
                    {remplir(t.joursRestants, {
                        jours: joursFactures({ joursParSemaine, semainesConges, annee }),
                        annee,
                    })}
                </p>
                <Avancer onClick={() => aller("couple")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "couple") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.couple}
                titre={t.qCouple}
                aide={t.qCoupleAide}
                choix={[
                    {
                        icone: "user-rounded-linear",
                        titre: t.seul,
                        detail: t.seulDetail,
                        onClick: () => {
                            setEnCouple(false);
                            aller("enfants");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.couple,
                        detail: t.coupleDetail,
                        onClick: () => {
                            setEnCouple(true);
                            aller("enfants");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "enfants") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.enfants}
                titre={t.qEnfants}
                aide={t.qEnfantsAide}
                choix={[0, 1, 2, 3].map((n) => ({
                    icone: n === 0 ? "close-circle-linear" : "user-plus-linear",
                    titre: t[`enfants${n}`],
                    onClick: () => {
                        setEnfants(n);
                        // La case T n'existe que pour une personne seule ayant
                        // au moins un enfant.
                        if (!enCouple && n >= 1) {
                            aller("caseT");
                            return;
                        }
                        setParentIsole(false);
                        aller("autres");
                    },
                }))}
            />
        );
    }

    if (etape === "caseT") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.caseT}
                titre={t.qCaseT}
                aide={t.qCaseTAide}
                choix={[
                    {
                        icone: "user-hand-up-linear",
                        titre: t.caseTOui,
                        detail: t.caseTOuiDetail,
                        onClick: () => {
                            setParentIsole(true);
                            aller("autres");
                        },
                    },
                    {
                        icone: "users-group-rounded-linear",
                        titre: t.caseTNon,
                        detail: t.caseTNonDetail,
                        onClick: () => {
                            setParentIsole(false);
                            aller("autres");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "autres") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.autres}
                titre={t.qAutres}
                aide={t.qAutresAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.autresNon,
                        detail: t.autresNonDetail,
                        onClick: () => {
                            setAutresRevenus(0);
                            aller("option");
                        },
                    },
                    {
                        icone: "wallet-money-linear",
                        titre: t.autresOui,
                        detail: t.autresOuiDetail,
                        onClick: () => {
                            setAutresRevenus(30000);
                            aller("montantAutres");
                        },
                    },
                ]}
            />
        );
    }

    if (etape === "montantAutres") {
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.montantAutres}
                titre={t.qMontantAutres}
                aide={t.qMontantAutresAide}
            >
                <Curseur
                    libelle={t.autresRevenus}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    maximum={120000}
                    pas={1000}
                    formater={(v) => euros(v, lang)}
                />
                <Avancer onClick={() => aller("option")}>{t.continuer}</Avancer>
            </Ecran>
        );
    }

    if (etape === "option") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES.option}
                titre={t.qOption}
                aide={t.qOptionAide}
                choix={[
                    {
                        icone: "close-circle-linear",
                        titre: t.optionNon,
                        detail: t.optionNonDetail,
                        onClick: () => {
                            setOptionFiscale("bareme");
                            aller("resultat");
                        },
                    },
                    {
                        icone: "check-circle-linear",
                        titre: t.optionOui,
                        detail: t.optionOuiDetail,
                        onClick: () => {
                            setOptionFiscale("vfl");
                            aller("resultat");
                        },
                    },
                ]}
            />
        );
    }

    const messageTva = {
        franchise: remplir(t.tvaFranchise, { base: euros(r.tva.base, lang) }),
        janvier: remplir(t.tvaJanvier, { base: euros(r.tva.base, lang) }),
        immediate: remplir(t.tvaImmediate, { majore: euros(r.tva.majore, lang) }),
    }[r.tva.statut];

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                <Curseur
                    libelle={t.objectif}
                    aide={t.objectifAide}
                    valeur={netMensuel}
                    poser={setNetMensuel}
                    minimum={500}
                    maximum={8000}
                    pas={100}
                    formater={(v) => euros(v, lang)}
                />
            </div>

            {r.depassePlafond ? (
                <div className="mt-6 rounded-xl p-5 border border-amber-500/50 bg-amber-500/[0.06]">
                    <div className="font-semibold text-amber-700 dark:text-amber-400 leading-snug">
                        {t.plafondTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {remplir(t.plafondTexte, {
                            ca: euros(r.chiffreAffaires, lang),
                            plafond: euros(r.plafond, lang),
                        })}
                    </p>
                    <p className="text-sm font-semibold dark:text-white mt-3 mb-0">
                        {remplir(t.plafondMax, { max: euros(r.netMensuelMaximum, lang) })}
                    </p>
                </div>
            ) : (
                <>
                    <div className="mt-6 rounded-xl p-5 border border-gray-200/70 dark:border-white/10">
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                            {t.tarif}
                        </div>
                        <div className="text-3xl font-bold tabular-nums dark:text-white">
                            <MontantExplicable explication={ex?.tarif} ouvert={ouverte === "tarif"} onBasculer={() => basculer("tarif")}>
                                <Montant valeur={r.tarifJour} lang={lang} reduit={reduit} />
                            </MontantExplicable>{" "}
                            <span className="text-base font-semibold text-gray-500 dark:text-lightBlue/70">
                                {t.parJour}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">
                            {remplir(t.tarifDetail, {
                                jours: r.jours,
                                ca: euros(r.chiffreAffaires, lang),
                                caMois: euros(r.chiffreAffaires / 12, lang),
                            })}
                        </div>
                        {ouverte === "tarif" && <PanneauExplication explication={ex?.tarif} lang={lang} />}
                    </div>

                    <IndiceExplication lang={lang} />

                    <div className="mt-3">
                        <Ligne
                            libelle={t.cotisations}
                            montant={r.cotisations}
                            couleur="bg-primary"
                            lang={lang}
                            reduit={reduit}
                            explication={ex?.cotisations}
                            ouvert={ouverte === "cotisations"}
                            onBasculer={() => basculer("cotisations")}
                        />
                        <Ligne
                            libelle={t.impot}
                            aide={t.impotAide}
                            montant={r.impot}
                            couleur="bg-amber-500"
                            lang={lang}
                            reduit={reduit}
                            explication={ex?.impot}
                            ouvert={ouverte === "impot"}
                            onBasculer={() => basculer("impot")}
                        />
                        <Ligne
                            libelle={t.cfp}
                            montant={r.cfp}
                            couleur="bg-gray-400 dark:bg-lightBlue/40"
                            lang={lang}
                            reduit={reduit}
                            explication={ex?.cfp}
                            ouvert={ouverte === "cfp"}
                            onBasculer={() => basculer("cfp")}
                        />
                        {r.frais > 0 && (
                            <Ligne
                                libelle={t.fraisLigne}
                                montant={r.frais}
                                couleur="bg-slate-500 dark:bg-slate-400"
                                lang={lang}
                                reduit={reduit}
                                explication={ex?.frais}
                                ouvert={ouverte === "frais"}
                                onBasculer={() => basculer("frais")}
                            />
                        )}
                    </div>

                    <div className="mt-6 rounded-xl p-5 border border-green-600/40 bg-green-600/[0.06]">
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mb-1">
                            {t.net}
                        </div>
                        <div className="text-3xl font-bold tabular-nums text-green-700 dark:text-green-400">
                            <MontantExplicable explication={ex?.net} ouvert={ouverte === "net"} onBasculer={() => basculer("net")}>
                                <Montant valeur={r.net} lang={lang} reduit={reduit} />
                            </MontantExplicable>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-lightBlue mt-1">
                            {remplir(t.netAide, { montant: euros(r.net / 12, lang) })}
                        </div>
                        {ouverte === "net" && <PanneauExplication explication={ex?.net} lang={lang} />}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-5 mb-0">
                        {messageTva}
                        {r.tva.statut !== "franchise" && ` ${t.tvaClients}`}
                    </p>
                </>
            )}

            {/* revenuNet ne les déclare dues qu'aux commerçants et aux artisans
                au-delà du seuil : un libéral n'en paie pas. */}
            {r.taxeChambreDue && (
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">
                    {remplir(t.taxeChambre, { seuil: euros(SEUIL_TAXE_CHAMBRE, lang) })}
                </p>
            )}
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">{t.note}</p>

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
